import { useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Button, Divider, IconButton, List, Paper, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';

import { AuthStoreContext } from '../../Root';
import NodeStore from '../../../stores/node-store';
import NodeView from '../../dashboard/node/NodeView';
import NodeItem, { NodeChildrenSkeleton, NodeLoadError } from '../../dashboard/node/NodeItem';
import DeviceView from '../../dashboard/device/DeviceView';
import AddDeviceDialog from './AddDeviceDialog';
import EditDeviceDialog from './EditDeviceDialog';
import MoveDeviceDialog from './MoveDeviceDialog';
import { DeviceDndContext, useDeviceDnd } from '../../../contexts/DeviceDndContext';
import { DeviceService } from '../../../http/device-service';
import { DeviceInfo, DeviceType } from '../../../types/device';
import { SensorType } from '../../../types/sensor';

interface PendingMove {
    device: DeviceInfo;
    targetNodeId: number;
    targetNodeName: string;
}

//=================================================================================================
interface AdminDeviceItemProps {
    device: DeviceInfo;
    depth: number;
    rootNodeId: number;
    onSaved: () => void;
}

function AdminDeviceItem({ device, depth, rootNodeId, onSaved }: AdminDeviceItemProps) {
    const dnd = useDeviceDnd();
    const [editOpen, setEditOpen] = useState(false);
    const info = device.info ?? {};

    const row = (
        <>
            <DeviceView
                deviceId={device.device_id}
                deviceType={(info.deviceType as DeviceType) ?? DeviceType.Device_Unknown}
                sensorType={(info.sensorType as SensorType) ?? SensorType.UnknownSensorType}
                sensorId={info.sensorId ?? 0}
                lastTransaction={info.lastTransaction ?? new Date().toISOString()}
                logConfig={info.logConfig}
                battery={info.battery}
                rssi={info.rssi}
                sensorState={info.sensorState}
                depth={depth}
                onClick={() => {}}
                actions={
                    <Tooltip title="Редактировать">
                        <IconButton size="small" onClick={() => setEditOpen(true)}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                }
            />
            <EditDeviceDialog
                device={device}
                open={editOpen}
                onClose={() => setEditOpen(false)}
                onSaved={onSaved}
                rootNodeId={rootNodeId}
            />
        </>
    );

    if (!dnd) return row;
    return (
        <div
            draggable
            onDragStart={() => dnd.onDragStart(device)}
            onDragEnd={() => dnd.onDragEnd()}
            style={{ opacity: dnd.draggedDevice?.device_id === device.device_id ? 0.4 : 1, cursor: 'grab' }}
        >
            {row}
        </div>
    );
}

//=================================================================================================
function DeviceManager() {
    const { authStore } = useContext(AuthStoreContext);
    const user = authStore.user;

    const [store] = useState(() => new NodeStore(user?.node_id ?? 0));
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [draggedDevice, setDraggedDevice] = useState<DeviceInfo | null>(null);
    const [pendingMove, setPendingMove] = useState<PendingMove | null>(null);
    const [refreshToken, setRefreshToken] = useState(0);

    useEffect(() => {
        if (!user) return;
        store
            .load()
            .catch(() => setError(true))
            .finally(() => setInitialLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (!user) return null;

    const handleAdded = () => {
        store.load().catch(() => {});
        setRefreshToken((k) => k + 1);
    };

    const handleDeviceSaved = () => {
        store.load().catch(() => {});
        setRefreshToken((k) => k + 1);
    };

    const handleDrop = (nodeId: number, nodeName: string) => {
        if (!draggedDevice || draggedDevice.node_id === nodeId) {
            setDraggedDevice(null);
            return;
        }
        setPendingMove({ device: draggedDevice, targetNodeId: nodeId, targetNodeName: nodeName });
        setDraggedDevice(null);
    };

    const handleMoveConfirm = async () => {
        if (!pendingMove) return;
        await DeviceService.move({ device_id: pendingMove.device.device_id, node_id: pendingMove.targetNodeId });
        store.load().catch(() => {});
        setRefreshToken((k) => k + 1);
        setPendingMove(null);
    };

    const dndContext = {
        draggedDevice,
        refreshToken,
        onDragStart: (device: DeviceInfo) => setDraggedDevice(device),
        onDragEnd: () => setDraggedDevice(null),
        onDrop: handleDrop,
    };

    let content: React.ReactNode;
    if (initialLoading) {
        content = <NodeChildrenSkeleton />;
    } else if (error) {
        content = <NodeLoadError />;
    } else {
        content = (
            <>
                {store.nodes.map((n) => (
                    <NodeItem
                        key={n.node_id}
                        node={n}
                        depth={1}
                        onSelect={() => {}}
                        renderDevice={(d, dep) => (
                            <AdminDeviceItem
                                device={d}
                                depth={dep}
                                rootNodeId={user.node_id}
                                onSaved={handleDeviceSaved}
                            />
                        )}
                    />
                ))}
                {store.devices.map((d) => (
                    <AdminDeviceItem
                        key={d.device_id}
                        device={d}
                        depth={1}
                        rootNodeId={user.node_id}
                        onSaved={handleDeviceSaved}
                    />
                ))}
            </>
        );
    }

    return (
        <DeviceDndContext.Provider value={dndContext}>
            <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2 }}>
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                            Управление устройствами
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Движение устройств в иерархия предприятия
                        </Typography>
                    </Box>
                    {user.isRoot() && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            disableElevation
                            onClick={() => setDialogOpen(true)}
                        >
                            Добавить
                        </Button>
                    )}
                </Box>
                <Divider />
                <List dense disablePadding sx={{ p: 1 }}>
                    <NodeView name="Всё" depth={0} defaultOpen onDrop={() => handleDrop(user.node_id, user.login)}>
                        {content}
                    </NodeView>
                </List>
            </Paper>
            {user.isRoot() && (
                <AddDeviceDialog
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    onAdded={handleAdded}
                    rootNodeId={user.node_id}
                />
            )}
            {pendingMove && (
                <MoveDeviceDialog
                    deviceId={pendingMove.device.device_id}
                    targetNodeName={pendingMove.targetNodeName}
                    open={Boolean(pendingMove)}
                    onClose={() => setPendingMove(null)}
                    onConfirm={handleMoveConfirm}
                />
            )}
        </DeviceDndContext.Provider>
    );
}

export default observer(DeviceManager);
